/**
 * ReferralModal Component
 * Feature 001: FA Dashboard Provider Referrals
 *
 * Modal for selecting clients to refer a service provider to
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Users, Search, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useClients } from '@/features/client-relationships';
import { useProviderReferrals } from '../hooks/useProviderReferrals';
import { useReferProvider } from '../hooks/useReferProvider';

type ReferralClient = {
  id: string;
  name: string;
  email: string;
  profilePhoto?: string;
};

type ReferralModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string | null;
  providerName: string;
  profileViewEngagementId?: string;
};

export function ReferralModal({
  open,
  onOpenChange,
  providerId,
  providerName,
  profileViewEngagementId,
}: ReferralModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  // Feature 102: required attestation + client-name opt-out (default on).
  const [attested, setAttested] = useState(false);
  const [shareClientName, setShareClientName] = useState(true);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(
    new Set(),
  );

  const clientsQuery = useClients();
  const { data: existingReferrals = [], isLoading: isLoadingReferrals } =
    useProviderReferrals(providerId);

  const clients: ReferralClient[] = useMemo(() => {
    return (clientsQuery.data?.data || []).map((c) => ({
      id: c.id,
      name: [c.firstName, c.lastName].filter(Boolean).join(' '),
      email: c.email,
      profilePhoto: c.profilePhoto,
    }));
  }, [clientsQuery.data]);

  const isLoadingClients = clientsQuery.isLoading;
  const { mutate: referProvider, isPending } = useReferProvider();

  // Get set of already-referred client IDs
  const alreadyReferredIds = useMemo(() => {
    return new Set(existingReferrals.map((ref) => ref.clientId));
  }, [existingReferrals]);

  // Filter clients by search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) {
      return clients;
    }

    const term = searchTerm.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(term) ||
        client.email.toLowerCase().includes(term),
    );
  }, [clients, searchTerm]);

  // Get counts
  const totalClients = clients.length;
  const filteredCount = filteredClients.length;
  const selectedCount = selectedClientIds.size;
  const alreadyReferredCount = alreadyReferredIds.size;

  // Handle select all (filtered clients only)
  const handleSelectAll = () => {
    const newSelected = new Set(selectedClientIds);
    filteredClients.forEach((client) => {
      if (!alreadyReferredIds.has(client.id)) {
        newSelected.add(client.id);
      }
    });
    setSelectedClientIds(newSelected);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedClientIds(new Set());
  };

  // Handle individual selection toggle
  const handleToggleClient = (clientId: string) => {
    const newSelected = new Set(selectedClientIds);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClientIds(newSelected);
  };

  // Handle submit
  const handleSubmit = () => {
    if (!providerId || selectedClientIds.size === 0 || !attested) return;

    referProvider(
      {
        providerId,
        clientIds: Array.from(selectedClientIds),
        profileViewEngagementId,
        attested,
        shareClientName,
      },
      {
        onSuccess: () => {
          // Reset and close
          setSelectedClientIds(new Set());
          setSearchTerm('');
          setAttested(false);
          setShareClientName(true);
          onOpenChange(false);

          // You could show a toast here if you have a toast system
        },
        onError: (error) => {
          console.error('Failed to send referrals:', error);
        },
      },
    );
  };

  // Handle close
  const handleClose = () => {
    if (!isPending) {
      setSelectedClientIds(new Set());
      setSearchTerm('');
      setAttested(false);
      setShareClientName(true);
      onOpenChange(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const isLoading = isLoadingClients || isLoadingReferrals;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Refer {providerName} to Clients</DialogTitle>
          <DialogDescription>
            Select which clients you'd like to refer this provider to. They'll
            receive an email with the provider's details.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading clients...</div>
          </div>
        ) : totalClients === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-gray-900 mb-2">No clients yet</h4>
            <p className="text-sm text-gray-500 mb-6 max-w-sm">
              You need to have connected clients before you can refer providers
              to them. Invite your first client to get started.
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <>
            {/* Search and Select All */}
            <div className="space-y-3 border-b pb-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectedCount > 0 ? (
                    <span className="font-medium text-[#026857]">
                      {selectedCount} selected
                    </span>
                  ) : (
                    <span>
                      {filteredCount} client{filteredCount !== 1 ? 's' : ''}
                      {alreadyReferredCount > 0 &&
                        ` (${alreadyReferredCount} already referred)`}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  {selectedCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </Button>
                  )}
                  {filteredCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </Button>
                  )}
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {selectedCount > 50 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You've selected {selectedCount} clients. Consider breaking
                    this into smaller batches for better deliverability.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Client List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {filteredClients.map((client) => {
                const isAlreadyReferred = alreadyReferredIds.has(client.id);
                const isSelected = selectedClientIds.has(client.id);
                const isDisabled = isAlreadyReferred;

                return (
                  <label
                    key={client.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      isDisabled
                        ? 'bg-gray-50 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'bg-[#026857]/5 border-[#026857]'
                          : 'hover:bg-gray-50 border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleToggleClient(client.id)}
                      className="w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857] disabled:cursor-not-allowed"
                    />

                    {client.profilePhoto ? (
                      <img
                        src={client.profilePhoto}
                        alt={client.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#026857]/10 flex items-center justify-center">
                        <span className="text-[#026857] font-semibold text-xs">
                          {client.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)}
                        </span>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {client.name}
                        </h4>
                        {isAlreadyReferred && (
                          <span className="flex items-center gap-1 text-xs text-[#026857] bg-[#026857]/10 px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Already referred
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {client.email}
                      </p>
                    </div>
                  </label>
                );
              })}

              {filteredCount === 0 && searchTerm && (
                <div className="text-center py-8 text-gray-500">
                  No clients found matching "{searchTerm}"
                </div>
              )}
            </div>

            {/* Feature 102: consent controls */}
            <div className="border-t pt-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={() => setAttested((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  I understand my name and profile link will be shared with{' '}
                  {providerName || 'this provider'} so they know who referred them.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareClientName}
                  onChange={() => setShareClientName((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  Share my client's first name and last initial with{' '}
                  {providerName || 'this provider'} (never their contact info)
                </span>
              </label>
            </div>

            {/* Footer */}
            <DialogFooter className="border-t pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={selectedCount === 0 || isPending || !attested}
                className="bg-[#026857] hover:bg-[#025247]"
              >
                {isPending
                  ? 'Sending...'
                  : `Send Referral${selectedCount !== 1 ? 's' : ''} (${selectedCount})`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
